import { observer } from 'mobx-react';
import { BrowserRouter } from 'react-router-dom';
import { FC, StrictMode, useEffect } from 'react';
import {
    TenantManagementApi,
    BASE_URL_TOKEN_TenantManagementApi,
} from '@servicetitan/marketing-tenant-management-api';
import {
    TelecomApi,
    BASE_URL_TOKEN_TelecomApi,
} from './modules/accounts-management/telecom/api/telecom.api';
import {
    ConfigurationManagementApi,
    BASE_URL_TOKEN_ConfigurationManagementApi,
} from './modules/accounts-management/api/configuration-management.api';
import { AnvilProvider } from '@servicetitan/anvil2';
import { Frame, Mask } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { AppStore } from './modules/common/stores/app.store';
import { LoadStatus } from './modules/common/enums/load-status';
import { Header } from './modules/common/components/header/header';
import { Routing } from './modules/common/components/routing/routing';
import { useAuthRedirect } from './modules/common/hooks/use-auth-redirect';
import { useBackendErrors } from './modules/common/hooks/use-backend-errors';
import { Notifications } from './modules/common/components/notifications/notifications';
import { CenteredError } from './modules/common/components/centered-error/centered-error';
import { AuthRedirection } from './modules/common/components/auth-redirection/auth-redirection';

import 'expose-loader?exposes=backendless|backendless!backendless';

import './design-system.css';
import './app.css';

export const Main: FC = provide({
    singletons: [
        AppStore,
        TenantManagementApi,
        TelecomApi,
        ConfigurationManagementApi,
        {
            useValue: process.env.API_URL,
            provide: BASE_URL_TOKEN_TenantManagementApi,
        },
        {
            useValue: process.env.API_URL,
            provide: BASE_URL_TOKEN_TelecomApi,
        },
        {
            useValue: process.env.API_URL,
            provide: BASE_URL_TOKEN_ConfigurationManagementApi,
        },
    ],
})(
    observer(() => {
        const [{ init, appLoadStatus }] = useDependencies(AppStore);

        useBackendErrors();

        const { isRedirecting } = useAuthRedirect();

        useEffect(() => {
            init();
        }, [init]);

        if (isRedirecting) {
            return <AuthRedirection />;
        }

        return (
            <Mask
                className="h-100vh"
                active={appLoadStatus !== LoadStatus.Ok}
                content={appLoadStatus === LoadStatus.Error ? <CenteredError /> : undefined}
            >
                {appLoadStatus === LoadStatus.Ok && (
                    <Frame header={<Header />}>
                        <Routing />
                    </Frame>
                )}
            </Mask>
        );
    }),
);

export const App: FC = () => (
    <StrictMode>
        <BrowserRouter>
            <Notifications>
                <AnvilProvider className="main-container">
                    <Main />
                </AnvilProvider>
            </Notifications>
        </BrowserRouter>
    </StrictMode>
);
